import { useEffect, useRef, useState } from 'react'
import './ContactMe.css'
import pic from './amro.webp'
import { CONTACT_HOVER, CONTACT_LINKS, CONTACT_SELECT } from './contactLinks'

const FLIP_HALF_MS = 350 // swap the back's content while the card is edge-on

const emit = (name, id) => window.dispatchEvent(new CustomEvent(name, { detail: { id } }))

const LinkIcon = ({ path }) => (
    <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d={path} />
    </svg>
)

// Contact panel. With the 3D scene running, the photo card and the four icons
// are 3D objects that fly in and land on the empty slots below
// (data-c3d-slot); otherwise the plain photo and SVG icons show here.
// Choosing a link flips the card to show how to reach me.
const ContactMe = () => {
    const [selected, setSelected] = useState(null)
    const [shown, setShown] = useState(null) // content on the card's back
    const [copied, setCopied] = useState(false)
    const primaryRef = useRef(null)
    const focusAfterFlip = useRef(false)

    // Keep the 3D card in sync; reset it when the panel closes
    useEffect(() => {
        emit(CONTACT_SELECT, selected)
    }, [selected])
    useEffect(() => () => emit(CONTACT_SELECT, null), [])

    // Back-face content: immediate when flipping from the photo, swapped
    // halfway through the spin when switching between two links
    const previous = useRef(null)
    useEffect(() => {
        const from = previous.current
        previous.current = selected
        setCopied(false)
        if (!selected) return undefined
        if (!from) {
            setShown(selected)
            return undefined
        }
        const timer = setTimeout(() => setShown(selected), FLIP_HALF_MS)
        return () => clearTimeout(timer)
    }, [selected])

    // Keyboard users land on the action after the flip
    useEffect(() => {
        if (!focusAfterFlip.current || !shown) return undefined
        const timer = setTimeout(() => {
            focusAfterFlip.current = false
            if (primaryRef.current) primaryRef.current.focus()
        }, FLIP_HALF_MS + 200)
        return () => clearTimeout(timer)
    }, [shown])

    const choose = (id, event) => {
        if (event.detail === 0) focusAfterFlip.current = true // keyboard "click"
        setSelected((current) => (current === id ? null : id))
    }

    const copy = async (text) => {
        try {
            await navigator.clipboard.writeText(text)
            setCopied(true)
            setTimeout(() => setCopied(false), 1600)
        } catch (error) {
            setCopied(false)
        }
    }

    const info = CONTACT_LINKS.find((link) => link.id === shown)

    return (
        <div className="c3d">
            <div className="c3d__card-area">
                <div
                    id="contact-card"
                    className={`c3d-card${selected ? ' is-flipped' : ''}`}
                    data-c3d-slot="card"
                >
                    <img
                        className="c3d-card__face c3d-card__front"
                        src={pic}
                        alt="Amro Salha"
                        width="480"
                        height="480"
                        decoding="async"
                    />
                    <div className="c3d-card__face c3d-card__back" aria-hidden={!selected} aria-live="polite">
                        {info && (
                            <>
                                <span className="c3d-back__label">{info.label}</span>
                                <p className="c3d-back__value">{info.value}</p>
                                <div className="c3d-back__actions">
                                    <a
                                        ref={primaryRef}
                                        className="c3d-btn c3d-btn--primary"
                                        href={info.href}
                                        {...(info.external ? { target: '_blank', rel: 'noreferrer' } : {})}
                                        tabIndex={selected ? 0 : -1}
                                    >
                                        {info.action}
                                        {info.external && <span aria-hidden="true"> ↗</span>}
                                    </a>
                                    {info.copy && (
                                        <button
                                            type="button"
                                            className="c3d-btn"
                                            onClick={() => copy(info.copy)}
                                            tabIndex={selected ? 0 : -1}
                                        >
                                            {copied ? 'Copied' : 'Copy'}
                                        </button>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="c3d-back__flip"
                                    onClick={() => setSelected(null)}
                                    tabIndex={selected ? 0 : -1}
                                >
                                    Show photo
                                </button>
                            </>
                        )}
                    </div>
                </div>
                <p className="c3d__hint" aria-hidden="true">
                    {selected ? 'Pick another link, or flip back to the photo' : 'Pick a link to flip the card'}
                </p>
            </div>

            <ul className="c3d-links" aria-label="Ways to reach me">
                {CONTACT_LINKS.map((link) => (
                    <li key={link.id}>
                        <button
                            type="button"
                            className={`c3d-chip${selected === link.id ? ' is-on' : ''}`}
                            aria-pressed={selected === link.id}
                            aria-controls="contact-card"
                            onClick={(event) => choose(link.id, event)}
                            onPointerEnter={() => emit(CONTACT_HOVER, link.id)}
                            onPointerLeave={() => emit(CONTACT_HOVER, null)}
                            onFocus={() => emit(CONTACT_HOVER, link.id)}
                            onBlur={() => emit(CONTACT_HOVER, null)}
                        >
                            <span className="c3d-chip__icon" data-c3d-slot={link.id}>
                                <LinkIcon path={link.path} />
                            </span>
                            <span className="c3d-chip__text">
                                <span className="c3d-chip__label">{link.label}</span>
                                <span className="c3d-chip__value">{link.value}</span>
                            </span>
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    )
}

export default ContactMe
